/**
 * @fileoverview Lightweight helpers for localStorage persistence.
 */

import { sanitizeSettings, sanitizeStats } from './settings-schema.js';

const STORAGE_KEYS = {
  settings: 'match3-vanilla.settings.v1',
  stats: 'match3-vanilla.stats.v1',
  highscores: 'match3-vanilla.highscores.v1',
};

export const MAX_HIGHSCORE_ENTRIES = 20;

function resolveDefaultStorage() {
  if (typeof window !== 'undefined' && window?.localStorage) {
    return window.localStorage;
  }

  // Graceful fallback for non-browser environments (tests)
  return {
    getItem: () => null,
    setItem: () => {},
    removeItem: () => {},
  };
}

let activeStorage = resolveDefaultStorage();

function readJson(storage, key) {
  const raw = storage.getItem(key);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) ?? null;
  } catch (_error) {
    return null;
  }
}

function writeJson(storage, key, value) {
  try {
    storage.setItem(key, JSON.stringify(value));
    return true;
  } catch (_error) {
    return false;
  }
}

export function normalizeHighscores(highscores) {
  if (!Array.isArray(highscores)) {
    return [];
  }

  return highscores
    .map((entry) => {
      if (!entry) {
        return null;
      }

      const name = typeof entry.name === 'string' ? entry.name.trim().slice(0, 20) : '';
      const score = Number(entry.score);
      const createdAt = Number(entry.createdAt) || 0;

      if (!name || !Number.isFinite(score) || score < 0) {
        return null;
      }

      return { name, score, createdAt };
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score || b.createdAt - a.createdAt)
    .slice(0, MAX_HIGHSCORE_ENTRIES);
}

function loadSettingsInternal(storage) {
  const stored = readJson(storage, STORAGE_KEYS.settings);
  return stored === null ? null : sanitizeSettings(stored);
}

function saveSettingsInternal(storage, settings) {
  if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
    return false;
  }

  const sanitized = sanitizeSettings(settings);
  return writeJson(storage, STORAGE_KEYS.settings, sanitized);
}

function loadStatsInternal(storage) {
  const stored = readJson(storage, STORAGE_KEYS.stats);
  return stored === null ? null : sanitizeStats(stored);
}

function saveStatsInternal(storage, stats) {
  if (!stats || typeof stats !== 'object' || Array.isArray(stats)) {
    return false;
  }

  const sanitized = sanitizeStats(stats);
  return writeJson(storage, STORAGE_KEYS.stats, sanitized);
}

function loadHighscoresInternal(storage) {
  const stored = readJson(storage, STORAGE_KEYS.highscores);
  if (!Array.isArray(stored)) {
    return null;
  }
  return normalizeHighscores(stored);
}

function saveHighscoresInternal(storage, highscores) {
  if (!Array.isArray(highscores)) {
    return false;
  }

  const normalized = normalizeHighscores(highscores);
  return writeJson(storage, STORAGE_KEYS.highscores, normalized);
}

function clearKeys(storage, keys) {
  keys.forEach((key) => storage.removeItem(key));
  return true;
}

// -----------------------------------------------------------------------------
// Public API - function based
// -----------------------------------------------------------------------------

export function configurePersistence({ storage } = {}) {
  activeStorage = storage ?? resolveDefaultStorage();
}

export function loadSettings() {
  return loadSettingsInternal(activeStorage);
}

export function saveSettings(settings) {
  return saveSettingsInternal(activeStorage, settings);
}

export function loadStats() {
  return loadStatsInternal(activeStorage);
}

export function saveStats(stats) {
  return saveStatsInternal(activeStorage, stats);
}

export function loadHighscores() {
  return loadHighscoresInternal(activeStorage);
}

export function saveHighscores(highscores) {
  return saveHighscoresInternal(activeStorage, highscores);
}

export function clearAll() {
  return clearKeys(activeStorage, Object.values(STORAGE_KEYS));
}

export function clearSettings() {
  return clearKeys(activeStorage, [STORAGE_KEYS.settings]);
}

export function clearStats() {
  return clearKeys(activeStorage, [STORAGE_KEYS.stats]);
}

export function clearHighscores() {
  return clearKeys(activeStorage, [STORAGE_KEYS.highscores]);
}

export function createPersistenceManager(storage = resolveDefaultStorage()) {
  return {
    loadSettings: () => loadSettingsInternal(storage),
    saveSettings: (settings) => saveSettingsInternal(storage, settings),
    loadStats: () => loadStatsInternal(storage),
    saveStats: (stats) => saveStatsInternal(storage, stats),
    loadHighscores: () => loadHighscoresInternal(storage),
    saveHighscores: (highscores) => saveHighscoresInternal(storage, highscores),
    clearAll: () => clearKeys(storage, Object.values(STORAGE_KEYS)),
    clearSettings: () => clearKeys(storage, [STORAGE_KEYS.settings]),
    clearStats: () => clearKeys(storage, [STORAGE_KEYS.stats]),
    clearHighscores: () => clearKeys(storage, [STORAGE_KEYS.highscores]),
  };
}

export const persistenceManager = {
  loadSettings,
  saveSettings,
  loadStats,
  saveStats,
  loadHighscores,
  saveHighscores,
  clearAll,
  clearSettings,
  clearStats,
  clearHighscores,
};

export default persistenceManager;
